import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Tag } from 'sonata-react-components';
import { DOOH_REPORT_OTA_LEVELS } from 'sonata-static-storage/lib/entities/doohReport/doohReport.constants';
import { getDoohReportOtaLevel } from 'sonata-static-storage/lib/helpers/doohReport.helpers';

import { useDoohReportFormatters } from '@/components/doohReport/common/hooks/useDoohReportFormatters';
import OtaBadge from '@/components/doohReport/common/OtaBadge/OtaBadge';
import OtaInfoModal from '@/components/doohReport/common/OtaInfoModal/OtaInfoModal';
import { getOtaScaleFillPct } from './otaScale.helpers';

import type { DoohReportOtaLevel } from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';

import './otaSummary.scss';

type Props = {
  /** Campaign OTA. Null when the coverage study did not calculate it. */
  ota: number | null;
};

/**
 * OTA panel of the summary card: the campaign's On-target Audience uplift, its rating
 * and the Low/Good/Strong/Excellent scale, with a link to the "What is OTA?" modal.
 */
export default function OtaSummary({ ota }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { formatOta } = useDoohReportFormatters();
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const level = getDoohReportOtaLevel(ota);
  const fillPct = getOtaScaleFillPct(ota);

  const levelLabels: Record<DoohReportOtaLevel, string> = {
    excellent: formatMessage({ defaultMessage: 'Excellent', id: 'doohReport.ota.level.excellent' }),
    good: formatMessage({ defaultMessage: 'Good', id: 'doohReport.ota.level.good' }),
    low: formatMessage({ defaultMessage: 'Low', id: 'doohReport.ota.level.low' }),
    strong: formatMessage({ defaultMessage: 'Strong', id: 'doohReport.ota.level.strong' })
  };

  return (
    <div className="otaSummary">
      <div className="otaSummary__header">
        <OtaBadge />
        <div className="otaSummary__value">
          <span className="otaSummary__ota">{formatOta(ota)}</span>
          {level && (
            <Tag id="otaSummary-level" status={level === 'low' ? 'warning' : 'ok'} tiny>
              {formatMessage(
                { defaultMessage: '{level} uplift', id: 'doohReport.ota.levelTag' },
                { level: levelLabels[level] }
              )}
            </Tag>
          )}
        </div>
        <div className="otaSummary__caption">
          <span className="otaSummary__subtitle">
            {formatMessage({ defaultMessage: 'On-target audience uplift', id: 'doohReport.ota.subtitle' })}
          </span>
          <button className="otaSummary__learnMore" onClick={() => setIsInfoOpen(true)} type="button">
            <i className="fa-regular fa-newspaper" />
            {formatMessage({ defaultMessage: 'Learn more...', id: 'doohReport.ota.learnMore' })}
          </button>
        </div>
      </div>

      <div className="otaSummary__scale">
        <div className="otaSummary__track">
          {fillPct !== null && <span className="otaSummary__fill" style={{ width: `${fillPct}%` }} />}
        </div>
        <div className="otaSummary__labels">
          {DOOH_REPORT_OTA_LEVELS.map(scaleLevel => (
            <span
              className={`otaSummary__label${scaleLevel === level ? ' otaSummary__label--current' : ''}`}
              key={scaleLevel}
            >
              {levelLabels[scaleLevel]}
            </span>
          ))}
        </div>
      </div>

      <OtaInfoModal id="otaSummary-infoModal" isOpen={isInfoOpen} onClose={() => setIsInfoOpen(false)} />
    </div>
  );
}
